import { useState } from 'react';
import { Box, Container, Grid, Typography, Card, CardMedia, Chip, Stack } from '@mui/material';

export function Gallery({ data }) {
  const primaryColor = data.primary_color || '#1e3a8a';
  const galleryItems = Array.isArray(data.gallery) ? data.gallery : [];

  if (galleryItems.length === 0) return null;

  const categories = ['All', ...new Set(galleryItems.map((item) => item.category || 'General'))];
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredItems = selectedCategory === 'All'
    ? galleryItems
    : galleryItems.filter((item) => item.category === selectedCategory);

  return (
    <Box id="gallery" sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f8fafc' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: 5 }}>
          <Box sx={{ display: 'inline-block', px: 2, py: 0.5, borderRadius: 4, backgroundColor: `${primaryColor}15`, color: primaryColor, mb: 1.5 }}>
            <Typography variant="caption" fontWeight={700} letterSpacing="0.05em">
              CAMPUS LIFE
            </Typography>
          </Box>
          <Typography variant="h2" fontWeight={800} color="#0f172a" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mb: 1 }}>
            Photo Gallery
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Glimpses of vibrant learning, sports events, and campus moments.
          </Typography>
        </Box>

        {/* Filter Categories */}
        {categories.length > 2 && (
          <Stack direction="row" spacing={1} useFlexGap sx={{ justifyContent: 'center', flexWrap: 'wrap', mb: 5 }}>
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                onClick={() => setSelectedCategory(cat)}
                sx={{
                  fontWeight: 700,
                  px: 1.5,
                  py: 2.2,
                  borderRadius: 3,
                  fontSize: '0.9rem',
                  backgroundColor: selectedCategory === cat ? primaryColor : '#ffffff',
                  color: selectedCategory === cat ? '#ffffff' : '#475569',
                  border: selectedCategory === cat ? 'none' : '1px solid #cbd5e1',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: selectedCategory === cat ? primaryColor : '#f1f5f9',
                  },
                }}
              />
            ))}
          </Stack>
        )}

        {/* Gallery Grid */}
        <Grid container spacing={3}>
          {filteredItems.map((item, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id || index}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.06)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
                    '& .gallery-img': {
                      transform: 'scale(1.06)',
                    },
                  },
                }}
              >
                <Box sx={{ overflow: 'hidden', height: 260, position: 'relative' }}>
                  <CardMedia
                    component="img"
                    image={item.image}
                    alt={item.title}
                    className="gallery-img"
                    sx={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.5s ease',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(to top, rgba(15, 23, 42, 0.8) 0%, rgba(0,0,0,0) 60%)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      p: 2.5,
                      color: '#ffffff',
                    }}
                  >
                    {item.category && (
                      <Typography variant="caption" fontWeight={700} sx={{ color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
                        {item.category}
                      </Typography>
                    )}
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem', lineHeight: 1.3 }}>
                      {item.title}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default Gallery;
